import { getSupabaseClient } from "../providers/supabase/supabase";

type GmailFailure = { ok: false; error?: string };

export type GmailSyncResult = {
  imported: number;
  googleEmail: string;
  finished: boolean;
};

const PAGE_LIMIT = 200;

const readFunctionError = async (error: unknown) => {
  const context =
    error && typeof error === "object" && "context" in error
      ? (error as { context?: unknown }).context
      : undefined;
  if (context instanceof Response) {
    try {
      const body = (await context.json()) as {
        error?: unknown;
        message?: unknown;
      };
      if (typeof body.error === "string" && body.error) return body.error;
      if (
        typeof body.message === "string" &&
        body.message &&
        body.message !== "Unauthorized"
      ) {
        return body.message;
      }
    } catch {
      return "Gmail could not be reached. Stay signed in to Vogelkop and try again.";
    }
  }
  return "Gmail could not be reached. Stay signed in to Vogelkop and try again.";
};

export async function gmailCall<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await getSupabaseClient().functions.invoke("gmail", {
    body,
  });
  const payload = data as
    | (T & { ok?: boolean; error?: string })
    | GmailFailure
    | null;
  if (payload && payload.ok === false) {
    throw new Error(payload.error || "Gmail could not be reached.");
  }
  if (error || !payload || payload.ok !== true) {
    throw new Error(await readFunctionError(error));
  }
  return payload;
}

type SyncPage = {
  ok: true;
  imported: number;
  nextPageToken: string | null;
  google_email: string;
};

export async function syncAllSentMail(): Promise<GmailSyncResult> {
  let imported = 0;
  let pageToken: string | null = null;
  let googleEmail = "";
  for (let page = 0; page < PAGE_LIMIT; page += 1) {
    const result: SyncPage = await gmailCall<SyncPage>({
      action: "sync",
      pageToken,
    });
    imported += result.imported ?? 0;
    googleEmail = result.google_email;
    if (!result.nextPageToken || result.nextPageToken === pageToken) {
      return { imported, googleEmail, finished: !result.nextPageToken };
    }
    pageToken = result.nextPageToken;
  }
  return { imported, googleEmail, finished: false };
}

const connections = new Map<
  string,
  Promise<GmailSyncResult & { googleEmail: string }>
>();

export function connectMailbox(code: string, state: string) {
  const key = `${state}:${code}`;
  const existing = connections.get(key);
  if (existing) return existing;
  const task = (async () => {
    const signedIn = await gmailCall<{ ok: true; google_email: string }>({
      action: "finish",
      code,
      state,
    });
    const synced = await syncAllSentMail();
    return {
      ...synced,
      googleEmail: signedIn.google_email || synced.googleEmail,
    };
  })();
  connections.set(key, task);
  return task;
}

export function sentMailSummary(result: GmailSyncResult) {
  const count = `${result.imported} sent ${result.imported === 1 ? "message" : "messages"}`;
  if (result.finished) {
    return `Imported ${count} onto matching leads.`;
  }
  return `Imported ${count}. Choose Sync sent mail to continue.`;
}
